import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Loader2, Pencil, Plus, UserPlus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Label from '../../components/ui/Label';
import Modal from '../../components/ui/Modal';
import Toggle from '../../components/ui/Toggle';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { FunctionalSelect, SelectItem } from '../../components/ui/Select';
import OrgRolesPicker, { orgIdsMissingRoles } from '../../components/OrgRolesPicker';
import UserAccessTags from '../../components/UserAccessTags';
import { useAuth } from '../../contexts/AuthContext';
import { getRoleCatalog } from '../../services/rolesService';
import { organizationsService } from '../../services/organizationsService';
import {
  EMAIL_RE,
  adminUsersApi,
  apiErrorText,
  compareUsersByName,
  fullName,
  sanitizeEmail,
  sanitizeName,
  sanitizeTitle,
} from './adminUsersApi';

const STATUS_FILTERS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'all', label: 'All' },
];
const KIND_FILTERS = [
  { value: 'all', label: 'Everyone' },
  { value: 'admin', label: 'Platform admins' },
  { value: 'bulk', label: 'Bulk contributors' },
  { value: 'user', label: 'Regular users' },
];

const EMPTY_FORM = { email: '', first_name: '', last_name: '', title: '' };

function hasRole(user, role) {
  return (user.memberships || []).some((m) => (m.roles || []).includes(role));
}

function matchesSearch(user, q) {
  if (!q) return true;
  const first = (user.first_name || '').toLowerCase();
  const last = (user.last_name || '').toLowerCase();
  const email = (user.email || '').toLowerCase();
  if (q.includes(' ')) {
    const [qFirst, qLast] = q.split(/\s+/, 2);
    return (!qFirst || first.startsWith(qFirst)) && (!qLast || last.startsWith(qLast));
  }
  return first.includes(q) || last.includes(q) || email.includes(q);
}

/**
 * Users listing for platform admins, laid out like the organization Members page: a "+" to add
 * a user, a search box, and a grid with each person's tags (platform grants and per-org roles),
 * an Active toggle and an edit link. Anything heavier (profile, access flags, organizations and
 * roles, password) lives on the detail page — granting platform admin deliberately takes more
 * than one click. Bulk Contributor is a FastCube role, chosen with the other roles.
 */
function AdminUsersList() {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('active');
  const [kind, setKind] = useState('all');
  const [orgFilter, setOrgFilter] = useState('all');

  const [confirm, setConfirm] = useState(null); // {title, message, confirmText, run}

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [orgIds, setOrgIds] = useState([]);
  const [orgRoles, setOrgRoles] = useState({});
  const [showRoleErrors, setShowRoleErrors] = useState(false);
  const [adding, setAdding] = useState(false);

  const loadUsers = useCallback(async () => {
    const rows = await adminUsersApi.list();
    setUsers(Array.isArray(rows) ? rows : []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([adminUsersApi.list(), adminUsersApi.organizations(), getRoleCatalog().catch(() => null)])
      .then(([rows, orgs, cat]) => {
        if (cancelled) return;
        setUsers(Array.isArray(rows) ? rows : []);
        setOrganizations(Array.isArray(orgs) ? orgs : []);
        setCatalog(cat);
      })
      .catch((err) => !cancelled && setError(apiErrorText(err, 'Failed to load users.')))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users
      .filter((u) => (status === 'all' ? true : status === 'active' ? u.is_active : !u.is_active))
      .filter((u) => {
        if (kind === 'admin') return u.is_admin;
        if (kind === 'bulk') return hasRole(u, 'bulk_contributor');
        if (kind === 'user') return !u.is_admin && !hasRole(u, 'bulk_contributor');
        return true;
      })
      .filter((u) => orgFilter === 'all' || (u.organizations || []).includes(orgFilter))
      .filter((u) => matchesSearch(u, q))
      .sort(compareUsersByName);
  }, [users, search, status, kind, orgFilter]);

  const patchUser = (id, patch) => setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));

  const run = async (id, fn, successMessage, failMessage) => {
    setBusyId(id);
    try {
      await fn();
      if (successMessage) toast.success(successMessage);
      organizationsService.clearOrganizationsListCookie();
    } catch (err) {
      toast.error(apiErrorText(err, failMessage));
    } finally {
      setBusyId(null);
    }
  };

  const toggleActive = (u, next) => {
    if (!next) {
      setConfirm({
        title: 'Deactivate user',
        message: `Deactivate ${u.email}? They will no longer be able to sign in and their API keys stop working.`,
        confirmText: 'Deactivate',
        run: () =>
          run(u.id, async () => {
            await adminUsersApi.setActive(u.id, false);
            patchUser(u.id, { is_active: false });
          }, 'User deactivated', 'Failed to deactivate user.'),
      });
      return;
    }
    run(u.id, async () => {
      await adminUsersApi.setActive(u.id, true);
      patchUser(u.id, { is_active: true });
    }, 'User activated', 'Failed to activate user.');
  };

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setFormErrors({});
    setOrgIds([]);
    setOrgRoles({});
    setShowRoleErrors(false);
    setAddOpen(true);
  };

  const toggleOrg = (oid, checked) => {
    setOrgIds(checked ? [...orgIds, oid] : orgIds.filter((x) => x !== oid));
  };

  const submitAdd = async () => {
    const errs = {};
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!EMAIL_RE.test(form.email.trim())) errs.email = 'Enter a valid email address';
    if (!form.first_name.trim()) errs.first_name = 'First name is required';
    if (!form.last_name.trim()) errs.last_name = 'Last name is required';
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;
    if (orgIds.length === 0) {
      toast.error('Select at least one organization.');
      return;
    }
    if (orgIdsMissingRoles(orgIds, orgRoles).length > 0) {
      setShowRoleErrors(true);
      toast.error('Pick at least one role for every organization.');
      return;
    }
    setAdding(true);
    try {
      const created = await adminUsersApi.create({
        email: form.email.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        title: form.title.trim(),
        organization_ids: orgIds,
        memberships: orgIds.map((oid) => ({ organization_id: String(oid), roles: orgRoles[oid] })),
      });
      organizationsService.clearOrganizationsListCookie();
      await loadUsers();
      setAddOpen(false);
      toast.success(`${created.email} added — an invite with a temporary password was sent`);
    } catch (err) {
      toast.error(apiErrorText(err, 'Failed to add user.'));
    } finally {
      setAdding(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle>
            Users
            {!loading ? <span className="ml-2 text-sm font-normal text-zinc-500">({visible.length} of {users.length})</span> : null}
          </CardTitle>
          <Button variant="outline" size="icon" onClick={openAdd} aria-label="Add user" title="Add user">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="text"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="min-w-[16rem] flex-1"
            aria-label="Search users"
          />
          <FunctionalSelect label="Status" className="w-40" value={status} onValueChange={setStatus}>
            {STATUS_FILTERS.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </FunctionalSelect>
          <FunctionalSelect label="Type" className="w-52" value={kind} onValueChange={setKind}>
            {KIND_FILTERS.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </FunctionalSelect>
          <FunctionalSelect label="Org" className="w-52" value={orgFilter} onValueChange={setOrgFilter} placeholder="All">
            <SelectItem value="all">All organizations</SelectItem>
            {[...organizations]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((o) => (
                <SelectItem key={o.id} value={o.name}>
                  {o.name}
                </SelectItem>
              ))}
          </FunctionalSelect>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500">Loading users…</p>
        ) : error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : visible.length === 0 ? (
          <p className="text-sm text-gray-500">{search.trim() ? 'No users match your search.' : 'No users match.'}</p>
        ) : (
          <div className="border border-gray-200 rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Name</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Email</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Roles</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Active</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700 border-b">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((u) => {
                  const isSelf = String(u.id) === String(me?.id);
                  const busy = busyId === u.id;
                  return (
                    <tr key={u.id} className={`border-b border-gray-100 hover:bg-gray-50 ${u.is_active ? '' : 'opacity-60'}`}>
                      <td className="px-3 py-2 align-top">
                        <button
                          type="button"
                          onClick={() => navigate(`/admin/users/${u.id}`)}
                          className="text-left font-medium text-[#18181B] hover:text-[#0062FB] hover:underline"
                        >
                          {fullName(u) || u.email}
                        </button>
                        {isSelf ? <span className="ml-1 text-xs text-zinc-400">(you)</span> : null}
                        {u.title ? <div className="text-xs text-zinc-500">{u.title}</div> : null}
                      </td>
                      <td className="px-3 py-2 align-top text-zinc-600">{u.email}</td>
                      <td className="max-w-[32rem] px-3 py-2 align-top">
                        <UserAccessTags user={u} catalog={catalog} />
                      </td>
                      <td className="px-3 py-2 align-top">
                        <Toggle
                          checked={Boolean(u.is_active)}
                          onChange={(next) => toggleActive(u, next)}
                          disabled={busy || isSelf}
                          label={u.is_active ? `Deactivate ${u.email}` : `Activate ${u.email}`}
                          title={isSelf ? 'You cannot deactivate your own account' : u.is_active ? 'Active — click to deactivate' : 'Inactive — click to activate'}
                        />
                      </td>
                      <td className="px-3 py-2 align-top text-right">
                        <button
                          type="button"
                          onClick={() => navigate(`/admin/users/${u.id}`)}
                          className="rounded p-1 text-[#0062FB] hover:bg-blue-50"
                          aria-label="Edit user"
                          title="Edit user"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Modal isOpen={addOpen} onClose={() => !adding && setAddOpen(false)} className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <CardContent className="space-y-4 pt-6">
          <h2 className="pr-8 text-lg font-semibold text-[#18181B]">Add a user</h2>
          <p className="text-sm text-zinc-500">
            Creates the account and emails an invite with a temporary password. Pick the organizations and their role in each.
          </p>
          <div className="space-y-2">
            <Label htmlFor="new-user-email">Email</Label>
            <Input
              id="new-user-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: sanitizeEmail(e.target.value) }))}
              placeholder="person@company.com"
              className={formErrors.email ? 'border-red-500' : ''}
            />
            {formErrors.email ? <p className="text-xs text-red-600">{formErrors.email}</p> : null}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="new-user-first">First name</Label>
              <Input id="new-user-first" value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: sanitizeName(e.target.value) }))} className={formErrors.first_name ? 'border-red-500' : ''} />
              {formErrors.first_name ? <p className="text-xs text-red-600">{formErrors.first_name}</p> : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-user-last">Last name</Label>
              <Input id="new-user-last" value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: sanitizeName(e.target.value) }))} className={formErrors.last_name ? 'border-red-500' : ''} />
              {formErrors.last_name ? <p className="text-xs text-red-600">{formErrors.last_name}</p> : null}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-user-title">Job title</Label>
            <Input id="new-user-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: sanitizeTitle(e.target.value) }))} placeholder="Optional" />
          </div>
          <div className="space-y-2">
            <Label>Organizations &amp; roles</Label>
            <OrgRolesPicker
              organizations={organizations}
              catalog={catalog}
              orgIds={orgIds}
              orgRoles={orgRoles}
              showErrors={showRoleErrors}
              onToggleOrg={toggleOrg}
              onRolesChange={(oid, roles) => setOrgRoles((prev) => ({ ...prev, [oid]: roles }))}
              idPrefix="new-user-org"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setAddOpen(false)} disabled={adding}>
              Cancel
            </Button>
            <Button variant="outline" onClick={submitAdd} disabled={adding} className="gap-1.5">
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              Add user &amp; send invite
            </Button>
          </div>
        </CardContent>
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={() => confirm?.run()}
        title={confirm?.title || 'Confirm'}
        message={confirm?.message || ''}
        confirmText={confirm?.confirmText || 'OK'}
        cancelText="Cancel"
      />
    </Card>
  );
}

export default AdminUsersList;
